'use client';

import { useState, useEffect } from 'react';

import Image from 'next/image';

import { CheckCircle, Minus, XCircle } from 'lucide-react';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { CustomerVideos } from '@/components/sections/customer-videos';
import { FaqSection } from '@/components/sections/faq-section';
import {
  BubbleAutopilot,
  BubbleInbound,
  SubpageHero,
} from '@/components/sections/subpage-hero';
import { TemplatesList } from '@/components/sections/templates-list';
import Testimonials from '@/components/sections/testimonials';
import { MagicalText } from '@/components/ui/magical-text';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTranslation } from '@/lib/i18n/context';
import { cn } from '@/lib/utils';

const CONTENT = {
  en: {
    eyebrow: 'Helpdesks',
    heroTitleA: 'Use Open directly in',
    heroTitleB: 'helpdesk',
    heroSubtitleA: 'No migration. Live in under an hour.',
    heroSubtitleB: 'Full features from day one — not months later.',
    inboundTitle: 'Refund request · #8842',
    inboundPreview: "Order #4521 hasn't shipped — can I get a refund?",
    autopilotTitle: 'Autopilot · ticket #8842',
    autopilotSteps: [
      'Read ticket in Zendesk',
      'Pulled order from Shopify',
      'Verified eligibility · 14 days old',
      'Refund issued · $89.00',
      'Closed in Zendesk · CSAT 5★',
    ],
    quoteIntroA: "Hey, I'm Mo, founder of Open.",
    quoteIntroB: "Don't take my word for it—companies like",
    automationWithOpen: 'automation with Open',
    quoteMid:
      'chose Open over Zendesk AI. They did it because Open works better for them.',
    quoteEnd: "They're obsessed with their customers and don't compromise.",
    role: 'Founder & CEO, Open',
    moPhotoAlt: 'Mo, Founder of Open',
    vs: 'vs',
  },
  ar: {
    eyebrow: 'أنظمة الدعم',
    heroTitleA: 'استخدم Open داخل نظام دعم',
    heroTitleB: 'اللي عندك',
    heroSubtitleA: 'بدون ترحيل. جاهز خلال أقل من ساعة.',
    heroSubtitleB: 'كل الميزات من أول يوم — مو بعد شهور.',
    inboundTitle: 'طلب استرداد · #8842',
    inboundPreview: 'الطلب #4521 ما انشحن لحد الآن — أقدر أسترجع فلوسي؟',
    autopilotTitle: 'Autopilot · تذكرة #8842',
    autopilotSteps: [
      'قرأ التذكرة في Zendesk',
      'سحب الطلب من Shopify',
      'تأكّد من الأهلية · عمر الطلب ١٤ يوم',
      'رجّع المبلغ · $89.00',
      'أقفل التذكرة في Zendesk · CSAT 5★',
    ],
    quoteIntroA: 'هلا، أنا Mo، مؤسس Open.',
    quoteIntroB: 'مو لازم تصدّقني — شركات مثل',
    automationWithOpen: 'أتمتة مع Open',
    quoteMid: 'اختاروا Open بدل Zendesk AI. ليش؟ لأنه يشتغل لهم أحسن.',
    quoteEnd: 'مهووسين بعملائهم، وما يقبلون أنصاف الحلول.',
    role: 'المؤسس والرئيس التنفيذي، Open',
    moPhotoAlt: 'Mo، مؤسس Open',
    vs: 'مقابل',
  },
} as const;

// Companies that switched from Zendesk (with descriptions)
const switchedCompanies = [
  {
    name: 'Mollie',
    logo: '/logos/logo-mollie.svg',
    description: '$6.5 billion payment processor',
    automationRate: '60%',
  },
  {
    name: 'FareHarbor',
    logo: '/logos/logo-fareharbor.svg',
    description: 'A Booking.com company with 16,000 customers',
    automationRate: '70%',
  },
  {
    name: 'Wafeq',
    logo: '/logos/logo-wafeq.svg',
    description: 'Leading accounting software in the Middle East',
  },
  {
    name: 'Trunkrs',
    logo: '/logos/logo-trunkrs.svg',
    description: '61,180+ Google reviews',
    automationRate: '80%',
  },
];

function SwitchedFromZendesk() {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % switchedCompanies.length);
        setIsAnimating(false);
      }, 400);
    }, 2500);

    return () => clearInterval(interval);
  }, [isPaused]);

  const currentCompany = switchedCompanies[currentIndex];

  return (
    <div className="mt-20">
      <div className="mx-auto max-w-3xl">
        <div className="flex flex-col items-start gap-6 md:flex-row md:gap-8">
          {/* Mo's photo */}
          <div className="shrink-0">
            <div className="relative">
              <img
                src="/about/image.png"
                alt={c.moPhotoAlt}
                className="h-20 w-20 rounded-full object-cover ring-4 ring-white shadow-xl md:h-24 md:w-24"
              />
              <div className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-black text-xs">
                👋
              </div>
            </div>
          </div>

          {/* Quote */}
          <div className="flex-1">
            <blockquote className="text-lg leading-relaxed text-neutral-700 md:text-xl">
              <span className="font-semibold text-neutral-900">
                {c.quoteIntroA}
              </span>{' '}
              {c.quoteIntroB}{' '}
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="relative mx-2 inline-flex h-6 w-24 cursor-pointer items-center justify-center align-middle md:h-7 md:w-28"
                      style={{
                        verticalAlign: 'middle',
                        transform: 'translateY(-2px)',
                      }}
                      onMouseEnter={() => setIsPaused(true)}
                      onMouseLeave={() => setIsPaused(false)}
                    >
                      <span
                        className={cn(
                          'absolute inset-0 flex items-center justify-center transition-all duration-400',
                          isAnimating
                            ? 'opacity-0 translate-y-2'
                            : 'opacity-100 translate-y-0',
                        )}
                      >
                        <img
                          src={currentCompany.logo}
                          alt={currentCompany.name}
                          className="max-h-5 max-w-full object-contain md:max-h-6"
                        />
                      </span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs px-4 py-3">
                    <p className="font-semibold text-white">
                      {currentCompany.name}
                    </p>
                    <p className="mt-1 text-sm text-neutral-300">
                      {(t.logos as Record<string, string>)[
                        currentCompany.description
                      ] || currentCompany.description}
                    </p>
                    {currentCompany.automationRate && (
                      <p className="mt-1.5 text-xs text-green-400">
                        {currentCompany.automationRate} {c.automationWithOpen}
                      </p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>{' '}
              {c.quoteMid}{' '}
              <span className="font-medium text-neutral-900">{c.quoteEnd}</span>
            </blockquote>
            <div className="mt-4 flex items-center gap-3">
              <span className="text-sm font-semibold text-neutral-900">
                Mo Gharbat
              </span>
              <span className="text-sm text-neutral-500">{c.role}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LocalizedZendeskPage() {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;
  const [hoveredModel, setHoveredModel] = useState<string | null>(null);

  const comparisonData = [
    {
      metric: t.helpdesk['Setup Time'],
      openAI: { value: t.helpdesk['Under 1 hour'], status: 'best' },
      zendesk: { value: t.helpdesk['+3 months'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Integration'],
      openAI: { value: t.helpdesk['Native Zendesk user'], status: 'best' },
      zendesk: { value: t.helpdesk['Separate system'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Avg Automation Rate'],
      openAI: { value: '77%', status: 'best' },
      zendesk: { value: '~50-60%', status: 'worst' },
    },
    {
      metric: t.helpdesk['Multi-modal AI'],
      openAI: {
        value: t.helpdesk['Voice, Vision, Text, Files'],
        status: 'best',
      },
      zendesk: { value: t.helpdesk['Text only (rest gated)'], status: 'worst' },
    },
    {
      metric: t.helpdesk['AI Quality'],
      openAI: { value: t.helpdesk['Multi-model engine'], status: 'best' },
      zendesk: { value: t.helpdesk['Single model'], status: 'worst' },
    },
    {
      metric: t.helpdesk['AI Quality Assurance'],
      openAI: { value: t.helpdesk['Built-in automatic QA'], status: 'best' },
      zendesk: { value: t.helpdesk['Manual QA or add-on'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Agentic Workflows'],
      openAI: { value: t.helpdesk['Drag-and-drop builder'], status: 'best' },
      zendesk: { value: t.helpdesk['Limited triggers'], status: 'worst' },
    },
    {
      metric: t.helpdesk['AI Outbound Sequencing'],
      openAI: {
        value: t.helpdesk['WhatsApp, Phone, Email, SMS'],
        status: 'best',
      },
      zendesk: { value: t.helpdesk['Not available'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Knowledge Gap Detection'],
      openAI: {
        value: t.helpdesk['Auto-detects & suggests content'],
        status: 'best',
      },
      zendesk: { value: t.helpdesk['Manual identification'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Customer Insights'],
      openAI: {
        value: t.helpdesk['Auto Jira tickets, trends'],
        status: 'best',
      },
      zendesk: { value: t.helpdesk['Basic reporting'], status: 'neutral' },
    },
    {
      metric: t.helpdesk['Pricing Model'],
      openAI: { value: t.helpdesk['Outcome-based'], status: 'best' },
      zendesk: { value: t.helpdesk['Per-agent + add-ons'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Reporting'],
      openAI: { value: t.helpdesk['Built-in ZD reports'], status: 'best' },
      zendesk: { value: t.helpdesk['Custom dashboard'], status: 'neutral' },
    },
    {
      metric: t.helpdesk['Learning Capability'],
      openAI: { value: t.helpdesk['Continuous'], status: 'best' },
      zendesk: { value: t.helpdesk['Manual updates'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Observability'],
      openAI: { value: t.helpdesk['Full transparency'], status: 'best' },
      zendesk: { value: t.helpdesk['Limited visibility'], status: 'worst' },
    },
  ];

  return (
    <>
      <SubpageHero
        eyebrow={c.eyebrow}
        title={
          <>
            {c.heroTitleA}{' '}
            <span className="whitespace-nowrap">
              <Image
                src="/helpdicks/zendesk.png"
                alt="Zendesk"
                width={48}
                height={48}
                className="mr-1 inline-block h-[0.8em] w-[0.8em] rounded-[0.18em] align-[-0.1em]"
              />
              <span className="text-[#03363D]">Zendesk</span>
            </span>{' '}
            {c.heroTitleB}
          </>
        }
        subtitle={
          <>
            {c.heroSubtitleA}
            <br />
            {c.heroSubtitleB}
          </>
        }
        videoSrc="/home/features/phone.mp4"
        bubbles={[
          {
            role: 'agent',
            widget: (
              <BubbleInbound
                source="Zendesk"
                sourceLogo="/helpdicks/zendesk.png"
                title={c.inboundTitle}
                preview={c.inboundPreview}
              />
            ),
          },
          {
            role: 'agent',
            widget: (
              <BubbleAutopilot
                title={c.autopilotTitle}
                steps={[...c.autopilotSteps]}
              />
            ),
          },
        ]}
      />

      <CustomerVideos />

      <BenefitsSection
        title={t.helpdesk['Get started in minutes']}
        subtitle={
          t.helpdesk[
            'Simple setup, powerful results. Your AI agent works alongside your team in'
          ] + ' Zendesk.'
        }
        learnMoreLink="#comparison"
        accentColor="emerald"
        theme="light"
        benefits={[
          {
            title: t.helpdesk['Create the AI agent in Zendesk'],
            description:
              t.helpdesk[
                'Our AI integrates directly into your Zendesk instance as a native agent.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/channels/zendesk/create-the-ai-agent.mp4',
            imagePosition: 'right',
          },
          {
            title: t.helpdesk['Set the routing rules'],
            description:
              t.helpdesk[
                "Route 100% or 10% of your support — it's really up to you."
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/channels/zendesk/set-the-routing-rules.mp4',
            imagePosition: 'left',
          },
          {
            title: t.helpdesk['Monitor and iterate'],
            description:
              t.helpdesk[
                'Because your new AI works within Zendesk, all of your reporting and analytics remain intact. Continuously improve with feedback and training.'
              ],
            linkText: '',
            linkHref: '#',
            imagePosition: 'right',
            statCard: {
              value: '77%',
              label: t.helpdesk['Automation Rate'],
              sublabel: t.helpdesk['Industry-leading performance'],
            },
          },
        ]}
      />

      {/* Comparison Section */}
      <section className="bg-white py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              <MagicalText>{t.helpdesk['Our AI']}</MagicalText> {c.vs}{' '}
              {t.helpdesk['Zendesk Ultimate AI']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.helpdesk[
                  "See how Open's AI engine compares to Zendesk's built-in AI solution."
                ]
              }
            </p>
          </div>
          <div className="relative overflow-hidden p-8">
            <div className="border-border/50 bg-background/50 relative overflow-hidden border backdrop-blur-sm">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead className="text-foreground font-semibold">
                      {t.helpdesk['Feature']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      <MagicalText>{t.helpdesk['Our AI']}</MagicalText>{' '}
                      {t.helpdesk['(Open)']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      {t.helpdesk['Zendesk Ultimate AI']}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonData.map((row, index) => (
                    <TableRow
                      key={index}
                      className="border-border/30 hover:bg-muted/30 transition-colors"
                    >
                      <TableCell className="text-foreground py-4 font-medium">
                        {row.metric}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'cursor-pointer py-4 text-center font-medium transition-all duration-300',
                          row.openAI.status === 'best'
                            ? 'bg-green-50 text-green-600 dark:bg-green-950/20'
                            : row.openAI.status === 'worst'
                              ? 'bg-red-50 text-red-600 dark:bg-red-950/20'
                              : 'bg-muted/50 text-foreground',
                          hoveredModel === 'openAI' &&
                            'bg-green-50/80 dark:bg-green-950/30',
                        )}
                        onMouseEnter={() => setHoveredModel('openAI')}
                        onMouseLeave={() => setHoveredModel(null)}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {row.openAI.status === 'best' && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                          {row.openAI.status === 'worst' && (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          {row.openAI.status === 'neutral' && (
                            <Minus className="text-muted-foreground h-4 w-4" />
                          )}
                          <span>{row.openAI.value}</span>
                        </div>
                      </TableCell>
                      <TableCell
                        className={cn(
                          'cursor-pointer py-4 text-center font-medium transition-all duration-300',
                          row.zendesk.status === 'best'
                            ? 'bg-green-50 text-green-600 dark:bg-green-950/20'
                            : row.zendesk.status === 'worst'
                              ? 'bg-red-50 text-red-600 dark:bg-red-950/20'
                              : 'bg-muted/50 text-foreground',
                          hoveredModel === 'zendesk' &&
                            'bg-red-50/80 dark:bg-green-950/30',
                        )}
                        onMouseEnter={() => setHoveredModel('zendesk')}
                        onMouseLeave={() => setHoveredModel(null)}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {row.zendesk.status === 'best' && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                          {row.zendesk.status === 'worst' && (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          {row.zendesk.status === 'neutral' && (
                            <Minus className="text-muted-foreground h-4 w-4" />
                          )}
                          <span>{row.zendesk.value}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Social Proof - Companies that chose Open over Zendesk AI */}
          <SwitchedFromZendesk />
        </div>
      </section>

      {/* Automate Zendesk Manual Work Section */}
      <section className="bg-neutral-50 py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              {t.helpdesk['Automate the manual tasks, not just the answers']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.helpdesk[
                  'In addition to fully resolving support tickets, Open automates the manual tasks that slow down your team—routing, tagging, QA, and more.'
                ]
              }
            </p>
          </div>
          <TemplatesList
            systemFilter="Zendesk"
            categoryFilter="Customer Support"
            limit={6}
            variant="slider"
          />
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection
        title={t.helpdesk['Frequently asked questions']}
        subtitle={
          t.helpdesk[
            "Find answers to common questions about our Zendesk AI agent. Can't find what you're looking for? Contact our support team."
          ]
        }
        items={[
          {
            question:
              t.helpdesk['How long does it take to set up Open with Zendesk?'],
            answer:
              t.helpdesk[
                'Open can be set up in under an hour. It connects directly to your Zendesk instance via API and starts resolving queries on day one using your existing help center content.'
              ],
          },
          {
            question: t.helpdesk['Do I need to migrate away from Zendesk?'],
            answer:
              t.helpdesk[
                "No migration required. Open connects via API and works seamlessly within your existing Zendesk setup. There's no disruption to your workflows—just faster, higher-quality support."
              ],
          },
          {
            question: t.helpdesk['Which Zendesk channels does Open support?'],
            answer:
              t.helpdesk[
                'Open works across all Zendesk channels including tickets, live chat, email, voice, messaging (web, mobile, WhatsApp, Facebook), and social media—delivering personalized support everywhere your customers reach out.'
              ],
          },
          {
            question:
              t.helpdesk['Can I test Open and Zendesk AI at the same time?'],
            answer:
              t.helpdesk[
                'Yes! We can set up Open for you in a way where you can run an A/B test and decide for yourself. Try it risk-free for 1 month.'
              ],
          },
          {
            question: t.helpdesk['Can I try Open risk-free?'],
            answer:
              t.helpdesk[
                'Absolutely. You can try Open risk-free for a month with free implementation. Our team will help you get set up and see results before you commit.'
              ],
          },
          {
            question:
              t.helpdesk[
                'How does Open integrate with my existing Zendesk workflows?'
              ],
            answer:
              t.helpdesk[
                'Open works as a native Zendesk user, respecting your existing triggers, automations, and macros. It follows your assignment rules, updates custom fields, and integrates with your current workflows without any changes required.'
              ],
          },
          {
            question:
              t.helpdesk[
                'Can I control what percentage of tickets Open handles?'
              ],
            answer:
              t.helpdesk[
                'Yes. You have full control over routing rules. You can set Open to handle 100%, 10%, or any percentage of your support tickets based on your preferences and support strategy.'
              ],
          },
          {
            question: t.helpdesk['How does Open hand off to human agents?'],
            answer:
              t.helpdesk[
                'Open updates Zendesk tickets, adds AI-generated summaries, and seamlessly hands off to human agents when needed—giving them full context to respond faster without frustrating back-and-forth.'
              ],
          },
          {
            question:
              t.helpdesk[
                'Can I see how Open is performing in my Zendesk reports?'
              ],
            answer:
              t.helpdesk[
                'Yes. Since Open works as a native Zendesk user, all your existing reporting tools work perfectly. You can track performance, see resolution rates, and monitor behavior just like any other agent.'
              ],
          },
          {
            question: t.helpdesk['How does Open learn and improve over time?'],
            answer:
              t.helpdesk[
                "Open features continuous learning capabilities with built-in evaluation tools. It learns from your team's feedback, analyzes historical tickets, and improves accuracy over time with observability tools that provide full transparency."
              ],
          },
          {
            question:
              t.helpdesk['What channels can Open handle simultaneously?'],
            answer:
              t.helpdesk[
                'Open is omnichannel and works across Zendesk tickets, live chat, email, voice, WhatsApp, Facebook Messenger, and other messaging channels—all from one unified AI engine.'
              ],
          },
          {
            question:
              t.helpdesk[
                'Can I configure Open for different brands or customer tiers?'
              ],
            answer:
              t.helpdesk[
                'Yes. Open uses synced Zendesk data to configure responses for each segment. Answers and handoffs automatically adjust by brand, customer tier, or support queue.'
              ],
          },
          {
            question: t.helpdesk['What kind of queries can Open resolve?'],
            answer:
              t.helpdesk[
                'Open is designed to handle complex queries with accuracy and reliability. It can resolve a wide range of customer questions, from simple FAQs to intricate technical issues, using your knowledge base and documentation.'
              ],
          },
          {
            question:
              t.helpdesk['Does Open require changes to my current setup?'],
            answer:
              t.helpdesk[
                'No. Open connects via API with no migration required. It works within your existing workflows, follows your assignment rules, and requires zero disruption to your current Zendesk configuration.'
              ],
          },
        ]}
      />

      <Testimonials />
    </>
  );
}
